import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Plus, X, ChevronRight, ChevronDown, Layers, Box, FileText, 
  ListTodo, Send, Trash2, AlertCircle, Check, RotateCcw, ClipboardPaste,
  FileSpreadsheet, Eye
} from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { toast } from 'sonner';

// Type configurations with colors and icons
const TYPE_CONFIG = {
  epic: {
    label: 'Epic',
    icon: Layers,
    color: 'var(--clr-brand-primary)',
    bgColor: 'rgba(57, 73, 171, 0.1)',
    nextType: 'feature',
    indent: 0
  },
  feature: {
    label: 'Feature',
    icon: Box,
    color: '#388E3C',
    bgColor: 'rgba(56, 142, 60, 0.1)',
    nextType: 'story', // or 'task' if story layer disabled
    indent: 1
  },
  story: {
    label: 'Story',
    icon: FileText,
    color: '#1976D2',
    bgColor: 'rgba(25, 118, 210, 0.1)',
    nextType: 'task',
    indent: 2
  },
  task: {
    label: 'Task',
    icon: ListTodo,
    color: '#607D8B',
    bgColor: 'rgba(96, 125, 139, 0.1)',
    nextType: null,
    indent: 3
  }
};

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

// Generate temporary IDs
let tempIdCounter = 0;
const generateTempId = () => `temp_${++tempIdCounter}_${Date.now()}`;

/**
 * Single row in the composer - represents one work item
 */
const ComposerRow = ({
  item,
  depth,
  storyLayerEnabled,
  onUpdate,
  onDelete,
  onAddChild,
  onKeyDown,
  focusedId,
  setFocusedId
}) => {
  const inputRef = useRef(null);
  const config = TYPE_CONFIG[item.type];
  const Icon = config?.icon || Layers;
  
  const indentPx = depth * 32;
  const canAddChild = config?.nextType && (item.type !== 'feature' || storyLayerEnabled || config.nextType === 'task');
  
  // Determine what child type to add
  const getChildType = () => {
    if (item.type === 'feature' && !storyLayerEnabled) {
      return 'task';
    }
    return config?.nextType;
  };
  
  useEffect(() => {
    if (focusedId === item.temp_id && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focusedId, item.temp_id]);
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onKeyDown(item, 'enter');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        onKeyDown(item, 'shift-tab');
      } else {
        onKeyDown(item, 'tab');
      }
    } else if (e.key === 'Backspace' && item.name === '') {
      e.preventDefault();
      onKeyDown(item, 'backspace');
    }
  };
  
  return (
    <div 
      className="flex items-center gap-2 py-2 px-3 hover:bg-[var(--clr-surface-hover)] rounded-lg group transition-colors"
      style={{ marginLeft: `${indentPx}px` }}
      data-testid={`composer-row-${item.temp_id}`}
    >
      {/* Type indicator */}
      <div 
        className="flex items-center justify-center w-7 h-7 rounded-md shrink-0"
        style={{ backgroundColor: config?.bgColor }}
      >
        <Icon className="w-4 h-4" style={{ color: config?.color }} />
      </div>
      
      {/* Name input */}
      <input
        ref={inputRef}
        type="text"
        value={item.name}
        onChange={(e) => onUpdate(item.temp_id, 'name', e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocusedId(item.temp_id)}
        placeholder={`New ${config?.label || 'Item'}...`}
        className="flex-1 px-3 py-1.5 bg-transparent border-0 focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:ring-opacity-30 rounded-md text-[var(--clr-text-primary)] placeholder:text-[var(--clr-text-muted)]"
        data-testid={`composer-input-${item.temp_id}`}
      />
      
      {/* Priority selector */}
      <select
        value={item.priority || 'Medium'}
        onChange={(e) => onUpdate(item.temp_id, 'priority', e.target.value)}
        className="px-2 py-1 text-xs border border-[var(--clr-border-light)] rounded-md bg-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        data-testid={`composer-priority-${item.temp_id}`}
      >
        {PRIORITIES.map(p => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
      
      {/* Add child button */}
      {canAddChild && (
        <button
          onClick={() => onAddChild(item, getChildType())}
          className="p-1.5 text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-active)] rounded-md opacity-0 group-hover:opacity-100 transition-all"
          title={`Add ${TYPE_CONFIG[getChildType()]?.label}`}
          data-testid={`composer-add-child-${item.temp_id}`}
        >
          <Plus className="w-4 h-4" />
        </button>
      )}
      
      {/* Delete button */}
      <button
        onClick={() => onDelete(item.temp_id)}
        className="p-1.5 text-[var(--clr-text-muted)] hover:text-[var(--clr-sev-blocked)] hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all"
        title="Remove"
        data-testid={`composer-delete-${item.temp_id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

/**
 * Task Parent Toggle - For XOR rule (Story OR Feature)
 */
const TaskParentToggle = ({ value, onChange, storyLayerEnabled }) => {
  if (!storyLayerEnabled) return null;
  
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-[var(--clr-surface-subtle)] rounded-lg mb-3">
      <span className="text-sm text-[var(--clr-text-secondary)]">Attach Tasks to:</span>
      <div className="flex bg-white rounded-md border border-[var(--clr-border-light)] p-0.5">
        <button
          onClick={() => onChange('story')}
          className={`px-3 py-1 text-sm rounded transition-colors ${
            value === 'story'
              ? 'bg-[var(--clr-brand-primary)] text-white'
              : 'text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)]'
          }`}
          data-testid="task-parent-toggle-story"
        >
          Story
        </button>
        <button
          onClick={() => onChange('feature')}
          className={`px-3 py-1 text-sm rounded transition-colors ${
            value === 'feature'
              ? 'bg-[var(--clr-brand-primary)] text-white'
              : 'text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)]'
          }`}
          data-testid="task-parent-toggle-feature"
        >
          Feature
        </button>
      </div>
      <span className="text-xs text-[var(--clr-text-muted)] ml-2">
        (XOR rule: Tasks can only have one parent type)
      </span>
    </div>
  );
};

/**
 * BulkPasteModal - Modal for pasting multiple items from spreadsheet
 */
const BulkPasteModal = ({ isOpen, onClose, onConfirm, storyLayerEnabled }) => {
  const [pasteContent, setPasteContent] = useState('');
  const [parsedItems, setParsedItems] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);
  
  // Parse pasted content from spreadsheet
  const parseSpreadsheetContent = (content) => {
    if (!content.trim()) {
      setParsedItems([]);
      setParseError(null);
      return;
    }
    
    const lines = content.trim().split('\n');
    const items = [];
    const errors = [];
    
    // Expected format: Type | Name | Priority | Status (tab or comma separated)
    // OR indentation-based: indentation determines hierarchy level
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;
      
      // Try tab-separated first, then comma-separated
      let parts = line.split('\t');
      if (parts.length < 2) {
        parts = line.split(',').map(p => p.trim());
      }
      
      // Check for indentation-based hierarchy
      const leadingSpaces = line.match(/^(\s*)/)[1].length;
      const indentLevel = Math.floor(leadingSpaces / 2);
      
      // Determine type from first column or indent level
      let type = 'task';
      let name = '';
      let priority = 'Medium';
      let status = 'Not Started';
      
      if (parts.length >= 2) {
        // Format: Type | Name | Priority | Status
        const typeCol = parts[0].trim().toLowerCase();
        if (['epic', 'feature', 'story', 'task'].includes(typeCol)) {
          type = typeCol;
          name = parts[1].trim();
          priority = parts[2]?.trim() || 'Medium';
          status = parts[3]?.trim() || 'Not Started';
        } else {
          // Just name provided, infer type from indent
          name = parts[0].trim();
          priority = parts[1]?.trim() || 'Medium';
          
          if (indentLevel === 0) type = 'epic';
          else if (indentLevel === 1) type = 'feature';
          else if (indentLevel === 2 && storyLayerEnabled) type = 'story';
          else type = 'task';
        }
      } else {
        name = line.trim();
        if (indentLevel === 0) type = 'epic';
        else if (indentLevel === 1) type = 'feature';
        else if (indentLevel === 2 && storyLayerEnabled) type = 'story';
        else type = 'task';
      }
      
      // Validate
      if (!name) {
        errors.push(`Line ${i + 1}: Name is required`);
        continue;
      }
      
      // Skip story if layer disabled
      if (type === 'story' && !storyLayerEnabled) {
        type = 'task';
      }
      
      items.push({
        temp_id: generateTempId(),
        type,
        name,
        priority: ['Low', 'Medium', 'High', 'Critical'].includes(priority) ? priority : 'Medium',
        status: status || 'Not Started',
        level: indentLevel,
        children: []
      });
    }
    
    if (errors.length > 0) {
      setParseError(errors.join('; '));
    } else {
      setParseError(null);
    }
    
    // Build hierarchy from flat list
    const hierarchy = buildHierarchyFromFlat(items);
    setParsedItems(hierarchy);
    setShowPreview(items.length > 0);
  };
  
  // Build nested hierarchy from flat items based on levels
  const buildHierarchyFromFlat = (flatItems) => {
    if (flatItems.length === 0) return [];
    
    const result = [];
    const stack = [{ level: -1, children: result }];
    
    for (const item of flatItems) {
      // Find parent at correct level
      while (stack.length > 1 && stack[stack.length - 1].level >= item.level) {
        stack.pop();
      }
      
      const parent = stack[stack.length - 1];
      const newItem = { ...item };
      delete newItem.level;
      
      parent.children.push(newItem);
      stack.push({ level: item.level, children: newItem.children });
    }
    
    return result;
  };
  
  const handlePaste = (e) => {
    const content = e.clipboardData?.getData('text') || '';
    setPasteContent(content);
    parseSpreadsheetContent(content);
  };
  
  const handleChange = (e) => {
    setPasteContent(e.target.value);
    parseSpreadsheetContent(e.target.value);
  };
  
  const handleConfirm = () => {
    if (parsedItems.length === 0) return;
    onConfirm(parsedItems);
    setPasteContent('');
    setParsedItems([]);
    setShowPreview(false);
    onClose();
  };
  
  const countItems = (items) => {
    let count = 0;
    for (const item of items) {
      count++;
      if (item.children) count += countItems(item.children);
    }
    return count;
  };
  
  if (!isOpen) return null;
  
  const totalCount = countItems(parsedItems);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            <h3 className="font-semibold text-[var(--clr-text-primary)]">Paste from Spreadsheet</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-5">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-3">
            Paste rows from Excel or Google Sheets. Format: <code className="bg-gray-100 px-1 rounded">Type | Name | Priority | Status</code> or use indentation for hierarchy.
          </p>
          
          <textarea
            ref={textareaRef}
            value={pasteContent}
            onChange={handleChange}
            onPaste={handlePaste}
            placeholder="Paste your spreadsheet data here...

Example formats:
epic	My Epic	High
feature	My Feature	Medium
task	My Task	Low

Or use indentation:
Epic Name
  Feature Name
    Story Name
      Task Name"
            className="w-full h-48 px-4 py-3 border border-[var(--clr-border-light)] rounded-lg text-sm font-mono resize-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-transparent"
            data-testid="bulk-paste-textarea"
          />
          
          {parseError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              {parseError}
            </div>
          )}
          
          {/* Preview */}
          {showPreview && parsedItems.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[var(--clr-text-primary)]">
                  Preview ({totalCount} items)
                </span>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-xs text-[var(--clr-text-muted)] hover:text-[var(--clr-text-secondary)]"
                >
                  Hide preview
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-3 bg-[var(--clr-surface-subtle)]">
                <PreviewTree items={parsedItems} />
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50">
          <span className="text-sm text-[var(--clr-text-muted)]">
            {totalCount > 0 ? `${totalCount} items ready to import` : 'No items parsed'}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-[var(--clr-text-secondary)] hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={totalCount === 0}
              className="px-4 py-2 text-sm bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
              data-testid="bulk-paste-confirm"
            >
              Import {totalCount} Items
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * PreviewTree - Renders a preview of parsed items
 */
const PreviewTree = ({ items, depth = 0 }) => {
  return (
    <div style={{ marginLeft: `${depth * 16}px` }}>
      {items.map(item => (
        <div key={item.temp_id} className="py-1">
          <div className="flex items-center gap-2 text-sm">
            <span 
              className="px-1.5 py-0.5 rounded text-xs font-medium"
              style={{ 
                backgroundColor: TYPE_CONFIG[item.type]?.bgColor,
                color: TYPE_CONFIG[item.type]?.color
              }}
            >
              {item.type}
            </span>
            <span className="text-[var(--clr-text-primary)]">{item.name}</span>
            <span className="text-xs text-[var(--clr-text-muted)]">{item.priority}</span>
          </div>
          {item.children?.length > 0 && (
            <PreviewTree items={item.children} depth={depth + 1} />
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * WorkItemComposer - Main component for creating work item hierarchies
 */
export const WorkItemComposer = ({
  projectId,
  tenantId,
  userId,
  storyLayerEnabled = true,
  onSuccess,
  onCancel,
  className = ''
}) => {
  const { canEdit } = useTenant();
  const [items, setItems] = useState([]);
  const [focusedId, setFocusedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [taskParentMode, setTaskParentMode] = useState('story');
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  
  // Initialize with one empty epic
  useEffect(() => {
    if (items.length === 0) {
      const initialItem = {
        temp_id: generateTempId(),
        type: 'epic',
        name: '',
        priority: 'Medium',
        children: []
      };
      setItems([initialItem]);
      setFocusedId(initialItem.temp_id);
    }
  }, []);
  
  // Handle bulk paste import
  const handleBulkPasteImport = (importedItems) => {
    if (importedItems.length === 0) return;
    
    // Merge imported items with existing items
    setItems(prev => {
      // If only one empty epic exists, replace it
      if (prev.length === 1 && !prev[0].name.trim()) {
        return importedItems;
      }
      // Otherwise append to existing
      return [...prev, ...importedItems];
    });
    
    toast.success(`${countItemsRecursive(importedItems)} items imported`);
  };
  
  const countItemsRecursive = (itemList) => {
    let count = 0;
    for (const item of itemList) {
      count++;
      if (item.children) count += countItemsRecursive(item.children);
    }
    return count;
  };
  
  // Flatten the tree for rendering
  const flattenItems = useCallback((itemList, depth = 0, parentId = null) => {
    const result = [];
    for (const item of itemList) {
      result.push({ ...item, depth, parentId });
      if (item.children?.length) {
        result.push(...flattenItems(item.children, depth + 1, item.temp_id));
      }
    }
    return result;
  }, []);
  
  const flatItems = flattenItems(items);
  
  // Find and update item in nested structure
  const updateItemInTree = (itemList, tempId, field, value) => {
    return itemList.map(item => {
      if (item.temp_id === tempId) {
        return { ...item, [field]: value };
      }
      if (item.children?.length) {
        return { ...item, children: updateItemInTree(item.children, tempId, field, value) };
      }
      return item;
    });
  };
  
  // Delete item from nested structure
  const deleteItemFromTree = (itemList, tempId) => {
    return itemList
      .filter(item => item.temp_id !== tempId)
      .map(item => ({
        ...item,
        children: item.children ? deleteItemFromTree(item.children, tempId) : []
      }));
  };
  
  // Add child to specific parent
  const addChildToItem = (itemList, parentTempId, childType) => {
    return itemList.map(item => {
      if (item.temp_id === parentTempId) {
        const newChild = {
          temp_id: generateTempId(),
          type: childType,
          name: '',
          priority: 'Medium',
          children: []
        };
        setFocusedId(newChild.temp_id);
        return { ...item, children: [...(item.children || []), newChild] };
      }
      if (item.children?.length) {
        return { ...item, children: addChildToItem(item.children, parentTempId, childType) };
      }
      return item;
    });
  };
  
  // Add sibling after specific item
  const addSiblingAfter = (itemList, tempId, siblingType) => {
    const result = [];
    for (const item of itemList) {
      result.push(item);
      if (item.temp_id === tempId) {
        const newSibling = {
          temp_id: generateTempId(),
          type: siblingType,
          name: '',
          priority: 'Medium',
          children: []
        };
        setFocusedId(newSibling.temp_id);
        result.push(newSibling);
      } else if (item.children?.length) {
        // Recursively check children but keep structure
        const updatedChildren = [];
        for (const child of item.children) {
          updatedChildren.push(child);
          if (child.temp_id === tempId) {
            const newSibling = {
              temp_id: generateTempId(),
              type: siblingType,
              name: '',
              priority: 'Medium',
              children: []
            };
            setFocusedId(newSibling.temp_id);
            updatedChildren.push(newSibling);
          }
        }
        result[result.length - 1] = { ...item, children: addSiblingAfterRecursive(item.children, tempId, siblingType) };
      }
    }
    return result;
  };
  
  const addSiblingAfterRecursive = (itemList, tempId, siblingType) => {
    const result = [];
    for (const item of itemList) {
      result.push({
        ...item,
        children: item.children ? addSiblingAfterRecursive(item.children, tempId, siblingType) : []
      });
      if (item.temp_id === tempId) {
        const newSibling = {
          temp_id: generateTempId(),
          type: siblingType,
          name: '',
          priority: 'Medium',
          children: []
        };
        setFocusedId(newSibling.temp_id);
        result.push(newSibling);
      }
    }
    return result;
  };
  
  // Handle item update
  const handleUpdate = (tempId, field, value) => {
    setItems(prev => updateItemInTree(prev, tempId, field, value));
  };
  
  // Handle item delete
  const handleDelete = (tempId) => {
    setItems(prev => deleteItemFromTree(prev, tempId));
  };
  
  // Handle add child
  const handleAddChild = (parent, childType) => {
    setItems(prev => addChildToItem(prev, parent.temp_id, childType));
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (item, action) => {
    const flatIndex = flatItems.findIndex(f => f.temp_id === item.temp_id);
    
    if (action === 'enter') {
      // Add sibling of same type
      setItems(prev => addSiblingAfterRecursive(prev, item.temp_id, item.type));
    } else if (action === 'tab' && TYPE_CONFIG[item.type]?.nextType) {
      // Indent: add as child
      const childType = item.type === 'feature' && !storyLayerEnabled ? 'task' : TYPE_CONFIG[item.type].nextType;
      handleAddChild(item, childType);
    } else if (action === 'shift-tab' && flatIndex > 0) {
      // Outdent: focus previous item
      const prevItem = flatItems[flatIndex - 1];
      setFocusedId(prevItem.temp_id);
    } else if (action === 'backspace' && item.name === '') {
      // Delete empty item
      handleDelete(item.temp_id);
      if (flatIndex > 0) {
        setFocusedId(flatItems[flatIndex - 1].temp_id);
      }
    }
  };
  
  // Convert flat structure to nested for API
  const buildHierarchyForApi = (itemList) => {
    return itemList
      .filter(item => item.name.trim())
      .map(item => ({
        type: item.type,
        name: item.name.trim(),
        temp_id: item.temp_id,
        priority: item.priority,
        children: item.children ? buildHierarchyForApi(item.children) : []
      }));
  };
  
  // Count items to be created
  const countItems = (itemList) => {
    let count = 0;
    for (const item of itemList) {
      if (item.name.trim()) count++;
      if (item.children) count += countItems(item.children);
    }
    return count;
  };
  
  const itemCount = countItems(items);
  
  // Submit handler
  const handleSubmit = async () => {
    if (!projectId) {
      toast.error('Please select a project first');
      return;
    }
    
    const hierarchy = buildHierarchyForApi(items);
    if (hierarchy.length === 0) {
      toast.error('Please add at least one item with a name');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.createWorkItemHierarchy({
        project_id: projectId,
        tenant_id: tenantId,
        user_id: userId,
        items: hierarchy
      });
      
      toast.success(`${response.data.created_count} work items created!`, {
        action: {
          label: 'Undo',
          onClick: () => {
            // TODO: Implement undo functionality
            toast.info('Undo not yet implemented');
          }
        }
      });
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      // Reset composer
      setItems([{
        temp_id: generateTempId(),
        type: 'epic',
        name: '',
        priority: 'Medium',
        children: []
      }]);
    } catch (error) {
      console.error('Failed to create hierarchy:', error);
      toast.error(error.response?.data?.detail || 'Failed to create work items');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Reset handler
  const handleReset = () => {
    setItems([{
      temp_id: generateTempId(),
      type: 'epic',
      name: '',
      priority: 'Medium',
      children: []
    }]);
    setFocusedId(null);
  };
  
  // Add new top-level epic
  const handleAddEpic = () => {
    const newEpic = {
      temp_id: generateTempId(),
      type: 'epic',
      name: '',
      priority: 'Medium',
      children: []
    };
    setItems(prev => [...prev, newEpic]);
    setFocusedId(newEpic.temp_id);
  };
  
  if (!canEdit()) {
    return (
      <div className={`bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-6 ${className}`}>
        <div className="flex items-center gap-3 text-[var(--clr-text-muted)]">
          <AlertCircle className="w-5 h-5" />
          <span>Upgrade to Business or Enterprise to create work items</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] ${className}`} data-testid="work-item-composer">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--clr-border-light)]">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[var(--clr-brand-primary)] bg-opacity-10 rounded-lg">
            <Layers className="w-5 h-5 text-[var(--clr-brand-primary)]" />
          </div>
          <div>
            <h3 className="font-semibold text-[var(--clr-text-primary)]">Work Item Composer</h3>
            <p className="text-sm text-[var(--clr-text-muted)]">
              Create Epic - Feature - {storyLayerEnabled ? 'Story - ' : ''}Task hierarchy
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-[var(--clr-text-muted)]">
          <span>Press <kbd className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] rounded text-xs">Enter</kbd> to add sibling</span>
          <span className="mx-1">|</span>
          <span><kbd className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] rounded text-xs">Tab</kbd> to add child</span>
        </div>
      </div>
      
      {/* Task Parent Toggle (XOR rule) */}
      {storyLayerEnabled && (
        <div className="px-5 pt-4">
          <TaskParentToggle
            value={taskParentMode}
            onChange={setTaskParentMode}
            storyLayerEnabled={storyLayerEnabled}
          />
        </div>
      )}
      
      {/* Composer rows */}
      <div className="px-3 py-4 max-h-[400px] overflow-y-auto">
        {flatItems.map(item => (
          <ComposerRow
            key={item.temp_id}
            item={item}
            depth={item.depth}
            storyLayerEnabled={storyLayerEnabled}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAddChild={handleAddChild}
            onKeyDown={handleKeyDown}
            focusedId={focusedId}
            setFocusedId={setFocusedId}
          />
        ))}
        
        {/* Add Epic button */}
        <button
          onClick={handleAddEpic}
          className="flex items-center gap-2 px-3 py-2 mt-2 text-sm text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
          data-testid="composer-add-epic"
        >
          <Plus className="w-4 h-4" />
          Add Epic
        </button>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-4 border-t border-[var(--clr-border-light)] bg-[var(--clr-surface-subtle)]">
        <div className="flex items-center gap-4">
          <span className="text-sm text-[var(--clr-text-muted)]">
            {itemCount} item{itemCount !== 1 ? 's' : ''} to create
          </span>
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)] hover:bg-white rounded-md transition-colors"
            data-testid="composer-reset"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={() => setShowBulkPaste(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--clr-brand-primary)] hover:bg-white rounded-md transition-colors"
            data-testid="composer-bulk-paste"
          >
            <ClipboardPaste className="w-4 h-4" />
            Paste from Spreadsheet
          </button>
        </div>
        
        <div className="flex items-center gap-3">
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-[var(--clr-text-secondary)] hover:bg-white rounded-lg transition-colors"
              data-testid="composer-cancel"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={submitting || itemCount === 0}
            className="flex items-center gap-2 px-5 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="composer-submit"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Creating...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Create All
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Bulk Paste Modal */}
      <BulkPasteModal
        isOpen={showBulkPaste}
        onClose={() => setShowBulkPaste(false)}
        onConfirm={handleBulkPasteImport}
        storyLayerEnabled={storyLayerEnabled}
      />
    </div>
  );
};

export default WorkItemComposer;
